import { ed25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';
import type { SessionCrypto } from './crypto.js';
import { StreamError } from './errors.js';
import { base64Decode, decodeText } from './platform.js';
import { parseSSEChunks } from './sse.js';
import type { Chunk, DiscloseField, Usage, WireAttestation, WireUsage } from './types.js';
import { fromWireUsage } from './types.js';

interface WsMessage {
  type: string;
  payload?: string;
  nonce?: string;
  usage?: WireUsage;
  error?: string;
  code?: string;
}

interface DoneDetail {
  usage: string; // raw JSON string — we need raw bytes for attestation hash
  attestation?: WireAttestation;
}

/**
 * InferenceStream reads encrypted inference response chunks from a WebSocket.
 * Implements AsyncIterableIterator<Chunk>.
 */
export class InferenceStream implements AsyncIterableIterator<Chunk> {
  private readonly ws: WebSocket;
  private readonly crypto: SessionCrypto;
  private readonly teePubKey: Uint8Array;
  private readonly _effectiveDisclose: DiscloseField[];
  private _usage: Usage | null = null;
  private _error: Error | null = null;
  private _done = false;
  private sseUsage: Usage | null = null;
  private verifiedUsage: Usage | null = null;

  // Message queue for bridging WebSocket events → async iteration
  private resolveNext: ((msg: WsMessage | null) => void) | null = null;
  private messageQueue: (WsMessage | null)[] = [];
  private queueError: Error | null = null;

  constructor(
    ws: WebSocket,
    crypto: SessionCrypto,
    teePubKey: Uint8Array,
    effectiveDisclose: DiscloseField[],
  ) {
    this.ws = ws;
    this.crypto = crypto;
    this.teePubKey = teePubKey;
    this._effectiveDisclose = effectiveDisclose;
    this.setupListeners();
  }

  private setupListeners(): void {
    const onMessage = (event: MessageEvent | { data: unknown }) => {
      try {
        const data = typeof event.data === 'string' ? event.data : String(event.data);
        const msg: WsMessage = JSON.parse(data);
        this.enqueue(msg);
      } catch (e) {
        this.queueError = e instanceof Error ? e : new Error(String(e));
        this.enqueue(null);
      }
    };

    const onError = (event: Event | { message?: string; error?: Error }) => {
      const errMsg = (event as { message?: string }).message ?? 'WebSocket error';
      this.queueError = new StreamError(errMsg);
      this.enqueue(null);
    };

    const onClose = () => {
      if (!this._done) {
        this.enqueue(null);
      }
    };

    // Support both browser WebSocket (addEventListener) and ws-style (on)
    if (typeof this.ws.addEventListener === 'function') {
      this.ws.addEventListener('message', onMessage as EventListener);
      this.ws.addEventListener('error', onError as EventListener);
      this.ws.addEventListener('close', onClose);
    } else {
      const wsNode = this.ws as unknown as {
        on(event: string, cb: (...args: unknown[]) => void): void;
      };
      wsNode.on('message', (data: unknown) => onMessage({ data }));
      wsNode.on('error', (err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        this.queueError = new StreamError(error.message);
        this.enqueue(null);
      });
      wsNode.on('close', onClose);
    }
  }

  private enqueue(msg: WsMessage | null): void {
    if (this.resolveNext) {
      const resolve = this.resolveNext;
      this.resolveNext = null;
      resolve(msg);
    } else {
      this.messageQueue.push(msg);
    }
  }

  private dequeue(): Promise<WsMessage | null> {
    if (this.messageQueue.length > 0) {
      return Promise.resolve(this.messageQueue.shift()!);
    }
    return new Promise<WsMessage | null>((resolve) => {
      this.resolveNext = resolve;
    });
  }

  async next(): Promise<IteratorResult<Chunk>> {
    if (this._done || this._error) {
      return { done: true, value: undefined };
    }

    while (true) {
      const msg = await this.dequeue();

      if (msg === null) {
        this._done = true;
        if (this.queueError) {
          this._error = this.queueError;
        }
        return { done: true, value: undefined };
      }

      switch (msg.type) {
        case 'chunk': {
          const ciphertext = base64Decode(msg.payload!);
          const nonce = base64Decode(msg.nonce!);
          const plaintext = this.crypto.decrypt(ciphertext, nonce);
          const text = decodeText(plaintext);
          const { chunk, usage } = parseSSEChunks(text);
          if (usage) this.sseUsage = usage;
          if (chunk.content || chunk.reasoningContent || (chunk.toolCalls && chunk.toolCalls.length > 0)) {
            return { done: false, value: chunk };
          }
          continue;
        }

        case 'done_detail': {
          const ciphertext = base64Decode(msg.payload!);
          const nonce = base64Decode(msg.nonce!);
          const plaintext = this.crypto.decrypt(ciphertext, nonce);
          const rawJson = plaintext; // Keep raw bytes for attestation hash

          const detail = JSON.parse(decodeText(plaintext));
          // Extract raw usage JSON bytes for attestation verification
          const rawUsageJson = this.extractRawUsageJson(decodeText(rawJson), detail);

          const usage = fromWireUsage(detail.usage);

          if (detail.attestation) {
            usage.attestation = {
              usageHash: detail.attestation.usage_hash,
              signature: detail.attestation.signature,
              sessionId: detail.attestation.session_id,
            };
            usage.verified = this.verifyAttestation(
              rawUsageJson,
              detail.attestation,
            );
          }

          this.verifiedUsage = usage;
          continue;
        }

        case 'done': {
          this._done = true;

          if (this.verifiedUsage) {
            this._usage = this.verifiedUsage;
            // Backfill from SSE if verified usage has zero tokens
            if (this.sseUsage && this._usage.totalTokens === 0 && this.sseUsage.totalTokens > 0) {
              this._usage.promptTokens = this.sseUsage.promptTokens;
              this._usage.completionTokens = this.sseUsage.completionTokens;
              this._usage.totalTokens = this.sseUsage.totalTokens;
            }
          } else {
            this._usage = msg.usage ? fromWireUsage(msg.usage) : null;
            if (this.sseUsage && (!this._usage || this._usage.totalTokens === 0)) {
              if (this._usage && this._usage.model) {
                this.sseUsage.model = this._usage.model;
              }
              this._usage = this.sseUsage;
            }
          }

          this.closeWs();
          return { done: true, value: undefined };
        }

        case 'error': {
          this._error = new StreamError(msg.error ?? 'Unknown server error', msg.code);
          this._done = true;
          this.closeWs();
          return { done: true, value: undefined };
        }

        default:
          continue;
      }
    }
  }

  /** Extract raw usage JSON bytes from the done_detail JSON string for attestation hash. */
  private extractRawUsageJson(jsonStr: string, _parsed: { usage: unknown }): Uint8Array {
    // We need the raw bytes of the "usage" field value.
    // Find "usage": and extract the JSON object.
    const idx = jsonStr.indexOf('"usage"');
    if (idx === -1) return new TextEncoder().encode(JSON.stringify(_parsed.usage));

    let start = jsonStr.indexOf(':', idx) + 1;
    // Skip whitespace
    while (start < jsonStr.length && ' \t\n\r'.includes(jsonStr[start])) start++;

    let depth = 0;
    let end = start;
    for (; end < jsonStr.length; end++) {
      if (jsonStr[end] === '{') depth++;
      else if (jsonStr[end] === '}') {
        depth--;
        if (depth === 0) { end++; break; }
      }
    }

    return new TextEncoder().encode(jsonStr.slice(start, end));
  }

  /** Verify TEE attestation signature over usage JSON. */
  private verifyAttestation(
    rawUsageJson: Uint8Array,
    att: WireAttestation,
  ): boolean {
    try {
      const hash = sha256(rawUsageJson);
      const hexDigest = bytesToHex(hash);

      if (hexDigest !== att.usage_hash) return false;

      const sig = base64Decode(att.signature);
      if (this.teePubKey.length !== 32) return false;

      return ed25519.verify(sig, hash, this.teePubKey);
    } catch {
      return false;
    }
  }

  private closeWs(): void {
    try {
      // Node.js ws: terminate() for immediate close (no close handshake wait)
      // Browser: close() for graceful close
      const wsAny = this.ws as unknown as { terminate?: () => void };
      if (typeof wsAny.terminate === 'function') {
        wsAny.terminate();
      } else {
        this.ws.close();
      }
    } catch {
      // ignore
    }
  }

  /** Token usage after the stream is complete. */
  get usage(): Usage | null {
    return this._usage;
  }

  /** Effective disclosure fields negotiated with the TEE. */
  get effectiveDisclose(): DiscloseField[] {
    return this._effectiveDisclose;
  }

  /** Error that stopped the stream, if any. */
  get error(): Error | null {
    return this._error;
  }

  /** Close the underlying WebSocket connection. */
  close(): void {
    this._done = true;
    this.closeWs();
  }

  [Symbol.asyncIterator](): AsyncIterableIterator<Chunk> {
    return this;
  }
}
